import React, { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const KEYS = [
  { key: "sms",              label: "SMS notifications",        group: "Channels" },
  { key: "email",            label: "Email notifications",      group: "Channels" },
  { key: "in_app",           label: "In-app notifications",     group: "Channels" },
  { key: "lesson_reminders", label: "Lesson reminders",         group: "Categories" },
  { key: "booking_updates",  label: "Booking updates",          group: "Categories" },
  { key: "lesson_changes",   label: "Lesson changes",           group: "Categories" },
  { key: "marketing",        label: "Marketing & product news", group: "Categories" },
];

async function authedFetch(path, opts = {}) {
  const token = localStorage.getItem("auth_token") || "";
  return fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
      ...(opts.headers || {}),
    },
  });
}

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authedFetch("/api/notifications/preferences");
        if (!r.ok) throw new Error();
        setPrefs(await r.json());
      } catch {
        setLoadError(true);
      }
    })();
  }, []);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      const r = await authedFetch("/api/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({ [key]: next[key] }),
      });
      if (!r.ok) throw new Error();
      const saved = await r.json();
      setPrefs(saved);
      toast.success(next[key] ? "Notification enabled" : "Notification disabled");
    } catch {
      setPrefs(prefs);
      toast.error("Preferences save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="notification-prefs-load-error">
        Couldn&apos;t load your preferences. Please refresh to try again.
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="notification-prefs-loading">
        Loading preferences…
      </div>
    );
  }

  const groups = ["Channels", "Categories"];

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-8" data-testid="notification-prefs-page">
      <header>
        <h1 className="text-2xl font-semibold">Notification preferences</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose which notifications reach you. Essential operational updates
          use the categories below — turn a channel or category off to stop
          new notifications in that group.
        </p>
      </header>

      {groups.map((group) => (
        <section key={group} className="space-y-3">
          <h2 className="text-lg font-medium">{group}</h2>
          {KEYS.filter((k) => k.group === group).map((k) => (
            <label
              key={k.key}
              className="flex items-center justify-between py-2 border-b last:border-0"
              data-testid={`notif-pref-row-${k.key}`}
            >
              <span>{k.label}</span>
              <input
                type="checkbox"
                checked={Boolean(prefs[k.key])}
                disabled={saving}
                onChange={() => toggle(k.key)}
                data-testid={`notif-pref-toggle-${k.key}`}
              />
            </label>
          ))}
        </section>
      ))}
    </div>
  );
}
